import {
	Ellipsis,
	Folder,
	LogOut,
	ReceiptEuro,
	SlidersHorizontal,
} from 'lucide-react'
import React from 'react'
import {Link, useLocation} from 'react-router-dom'
import {BasicVersionDto} from 'types'
import {
	Avatar,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
} from 'ui-v2'
import {
	useGetCurrentWorkspace,
	useSwitchWorkspace,
} from '~/api-client/workspace-client/workspaceHooks'
import {
	CURRENT_WORKSPACE,
	WORKSPACE_QUERY_KEY,
} from '~/api-client/workspace-client/workspaceQueryOptions'
import vahterusLogo from '~/assets/imgs/vahterus-logo-black.png'
import {useAuthenticated} from '~/hooks/useAuthenticated'
import {useWorkspaceActions, useWorkspaceState} from '~/hooks/useWorkspace'
import {getFeedbackItemGroup} from './NavItems'
import {WorkspaceSwitcher} from './WorkspaceSwitcher'

export type VersionGroups = Record<string, PureVersion[]>

export type PureVersion = Omit<BasicVersionDto, 'price_sheets'>

export function MenuView() {
	const {user, logout} = useAuthenticated()
	const {pathname} = useLocation()
	const {data: currentWorkspace, isFetching: isCurrentWorkspaceLoading} =
		useGetCurrentWorkspace({
			queryKey: [WORKSPACE_QUERY_KEY, CURRENT_WORKSPACE],
			refetchOnWindowFocus: false,
		})
	const {favorites} = useWorkspaceState()
	const {setIsWorkspaceLoading} = useWorkspaceActions()
	const {mutateAsync, isPending: isSwitching} = useSwitchWorkspace()
	const currentWorkspaceId = currentWorkspace?.id

	const currentTenant = React.useMemo(() => {
		if (!currentWorkspace) return ''

		return currentWorkspace.currentWorkspace
	}, [currentWorkspace])

	const tenantOptions = React.useMemo(() => {
		if (!currentWorkspace || !currentTenant) return []

		const currentTenantOption = currentWorkspace.groups.find(
			group => group.id === currentTenant,
		)

		if (!currentTenantOption) return []

		const options = [currentTenantOption]
		const localGroups = [...currentWorkspace.groups]

		localGroups
			.sort(a => (favorites.has(a.id) ? -1 : 1))
			.forEach(item => {
				if (options.length < 6 && item.id !== currentTenant) {
					options.push(item)
				}
			})

		return options.map(option => ({
			value: option.id,
			label: option.name,
		}))
	}, [currentWorkspace, currentTenant, favorites])

	const handleSelectWorkspace = (value: string) => {
		if (value === currentTenant) return

		mutateAsync({workspace: value})
	}

	//Temporary solution. Waiting for Tanstack router hook.

	const handleLogout = async () => {
		// The menu is only shown to a logged-in user, for whom oidc-spa sets logout
		logout!({
			redirectTo: 'specific url',
			url: import.meta.env.VITE_PRICING_WEBAPP_BASE_URL,
		})
	}

	React.useEffect(() => {
		if (isCurrentWorkspaceLoading || isSwitching) {
			setIsWorkspaceLoading(true)
		} else {
			setIsWorkspaceLoading(false)
		}
	}, [isCurrentWorkspaceLoading, isSwitching, setIsWorkspaceLoading])

	return (
		<Sidebar variant="inset" collapsible="icon">
			<SidebarHeader vahterusLogo={vahterusLogo} />
			<SidebarContent>
				<SidebarGroup>
					<SidebarMenu>
						<SidebarMenuItem>
							<WorkspaceSwitcher
								value={currentWorkspaceId}
								options={tenantOptions}
								isSwitching={isSwitching || isCurrentWorkspaceLoading}
								onSelect={handleSelectWorkspace}
							/>
						</SidebarMenuItem>
						<SidebarMenuItem>
							<Link to={'/cpq/offer/library/my'}>
								<SidebarMenuButton
									asChild
									icon={<Folder />}
									label={'Offer Library'}
									isActive={pathname.includes('/cpq/offer/library')}
								/>
							</Link>
						</SidebarMenuItem>
					</SidebarMenu>
				</SidebarGroup>
				<SidebarGroup className="border-t border-stroke-divider">
					<SidebarGroupLabel>Tools</SidebarGroupLabel>
					<SidebarMenu>
						<SidebarMenuItem>
							<Link to={'/cpq/tool/single-calculator'}>
								<SidebarMenuButton
									asChild
									icon={<SlidersHorizontal />}
									label={'Sizing calculation'}
									isActive={pathname.includes('/cpq/tool/single-calculator')}
								/>
							</Link>
						</SidebarMenuItem>
						<SidebarMenuItem>
							<Link to={'/pricing'}>
								<SidebarMenuButton
									asChild
									icon={<ReceiptEuro />}
									label={'Price lists'}
									isActive={pathname.includes('/pricing')}
								/>
							</Link>
						</SidebarMenuItem>
					</SidebarMenu>
				</SidebarGroup>
			</SidebarContent>
			<SidebarFooter>
				<SidebarGroup className="border-y border-stroke-divider">
					<SidebarGroupLabel>Help</SidebarGroupLabel>
					<SidebarMenu>
						{getFeedbackItemGroup([]).map(item => (
							<SidebarMenuItem key={item.name}>
								<a href={item.href} target="_blank" rel="noopener noreferrer">
									<SidebarMenuButton
										asChild
										icon={item.icon}
										label={item.label}
									/>
								</a>
							</SidebarMenuItem>
						))}
					</SidebarMenu>
				</SidebarGroup>
				<SidebarMenu>
					<SidebarMenuItem>
						<DropdownMenu>
							<DropdownMenuTrigger asChild disabled={isSwitching}>
								<SidebarMenuButton
									icon={
										<Avatar
											type={'initial'}
											shape={'round'}
											color={'grey'}
											username={user?.name || 'User Name'}
											size="medium"
										/>
									}
									hideTooltip
									label={user?.name || 'User Name'}
									className="w-full justify-between truncate"
									cy-data="workspaceSwitcher"
								>
									<Ellipsis className="min-h-6 min-w-6 h-6 w-6" />
								</SidebarMenuButton>
							</DropdownMenuTrigger>
							<DropdownMenuContent className="w-fit">
								<DropdownMenuItem
									label={'Log out'}
									onClick={() => handleLogout()}
									className="cursor-pointer"
									icon={<LogOut strokeWidth={1.25} height={16} width={16} />}
								/>
							</DropdownMenuContent>
						</DropdownMenu>
					</SidebarMenuItem>
				</SidebarMenu>
			</SidebarFooter>
		</Sidebar>
	)
}
